import React, { useState } from 'react';
import './LoginPage.css';

/**
 * O campo de senha, com o olho de mostrar e ocultar (05/10).
 *
 * ── Por que o olho ───────────────────────────────────────────────────────
 *
 * No celular a senha é digitada num teclado pequeno e sem ver o que saiu. Sem
 * poder conferir, o erro de digitação só aparece no login seguinte, como
 * "senha incorreta" — e na criação de conta ele vira uma senha que a pessoa
 * não sabe qual é.
 *
 * ── Um componente para os cinco campos ───────────────────────────────────
 *
 * Entrar, criar conta, escolher a senha (dois campos) e trocar a senha
 * (dois). Cinco cópias do mesmo botão divergem no primeiro ajuste.
 *
 * ── O botão não é um passo do Tab que atrapalha ──────────────────────────
 *
 * `type="button"`, senão ele ENVIA o formulário ao ser tocado. E
 * `aria-pressed` com um rótulo que diz a ação: quem usa leitor de tela ouve
 * "Mostrar senha, botão, não pressionado", e não só "botão".
 *
 * Mostrar não muda o `autocomplete`: o gerenciador de senhas do aparelho
 * continua reconhecendo o campo como senha.
 */
interface Props {
  rotulo: string;
  valor: string;
  aoMudar: (v: string) => void;
  /** `current-password` para entrar; `new-password` para criar ou trocar. */
  autoComplete: 'current-password' | 'new-password';
  desabilitado?: boolean;
  minLength?: number;
  /** A linha pequena abaixo do campo, como "Pelo menos 8 caracteres." */
  dica?: string;
  autoFocus?: boolean;
}

const CampoDeSenha: React.FC<Props> = ({
  rotulo, valor, aoMudar, autoComplete, desabilitado, minLength, dica, autoFocus,
}) => {
  const [visivel, setVisivel] = useState(false);

  return (
    <label className="login-campo">
      <span>{rotulo}</span>
      <span className="login-senha-caixa">
        <input
          type={visivel ? 'text' : 'password'}
          autoComplete={autoComplete}
          autoCapitalize="none" autoCorrect="off" spellCheck={false}
          minLength={minLength}
          value={valor} onChange={e => aoMudar(e.target.value)}
          disabled={desabilitado} required autoFocus={autoFocus}
        />
        <button
          type="button"
          className="login-senha-olho"
          aria-pressed={visivel}
          aria-label={visivel ? 'Ocultar senha' : 'Mostrar senha'}
          title={visivel ? 'Ocultar senha' : 'Mostrar senha'}
          onClick={() => setVisivel(v => !v)}
          disabled={desabilitado}
        >
          {/* O mesmo desenho do olhinho de "Ocultar valores" do cabeçalho. */}
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z"
                  stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
            <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
            {visivel && <path d="M4 4l16 16" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />}
          </svg>
        </button>
      </span>
      {dica && <small>{dica}</small>}
    </label>
  );
};

export default CampoDeSenha;
